import * as THREE from 'three';
import type { SfxName } from '../content/types';
import type { Random } from '../core/Random';
import type { Player } from '../entities/Player';
import { die, dieFaceUp } from '../world/builders/gameProps';
import type { Vec2 } from '../world/layout';
import type { Stage, StageButton, StageHud } from './types';

interface Rolling {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
  final: THREE.Quaternion;
  settled: boolean;
}

const ROLL = 2.0;
const SHOW = 2.2;

/**
 * Shooting dice with another player, on the ground between you: both pairs tumble out,
 * bounce and come to rest on the numbers both your secrets picked.
 */
export class DiceStage implements Stage {
  readonly title = 'Shootin’ Dice';
  readonly crosshair = false;
  readonly pointerLock = false;
  private t = 0;
  private readonly dice: Rolling[] = [];
  private readonly spot = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private bounced = 0;

  constructor(
    private readonly player: Player,
    /** Where they're standing, if we can see them. */
    private readonly them: Vec2 | null,
    private readonly mine: readonly [number, number],
    private readonly theirs: readonly [number, number],
    private readonly name: string,
    private readonly parent: THREE.Object3D,
    private readonly rng: Random,
    private readonly sfx: (n: SfxName) => void,
  ) {}

  get done(): boolean {
    return this.t > ROLL + SHOW;
  }

  hint(): string {
    return this.t < ROLL ? 'Come on, baby needs a new pair of Crocs...' : '';
  }

  buttons(): StageButton[] {
    return [];
  }

  begin(): void {
    const p = this.player.position;
    // On the ground between you two (or right in front of you).
    if (this.them) this.spot.set((p.x + this.them.x) / 2, 0, (p.z + this.them.z) / 2);
    else this.spot.set(p.x + Math.sin(this.player.facing) * 1.3, 0, p.z + Math.cos(this.player.facing) * 1.3);
    const throwFrom = (from: Vec2, values: readonly number[], color: string, pip: string): void => {
      values.forEach((n, i) => {
        const m = die(color, pip);
        m.position.set(from.x, 1.0, from.z);
        this.parent.add(m);
        const land = new THREE.Vector3(
          this.spot.x + this.rng.range(-0.35, 0.35) + (i - 0.5) * 0.15,
          0,
          this.spot.z + this.rng.range(-0.35, 0.35),
        );
        const vel = land
          .sub(m.position)
          .multiplyScalar(1 / 0.55)
          .setY(2.2);
        const yaw = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          this.rng.range(0, 6.28),
        );
        this.dice.push({
          mesh: m,
          vel,
          spin: new THREE.Vector3(this.rng.range(-14, 14), this.rng.range(-14, 14), this.rng.range(-14, 14)),
          final: yaw.multiply(dieFaceUp(n)),
          settled: false,
        });
      });
    };
    throwFrom(p, this.mine, '#f4f1e8', '#1b1b1b');
    throwFrom(
      this.them ?? { x: this.spot.x * 2 - p.x, z: this.spot.z * 2 - p.z },
      this.theirs,
      '#c62828',
      '#ffffff',
    );
    this.player.scripted = true;
    this.player.anim.update({ dt: 1, time: 0, speed: 0, pose: 'throw' });
    this.sfx('scratch');
  }

  update(dt: number): void {
    this.t += dt;
    this.player.anim.update({ dt, time: this.t, speed: 0, pose: this.t < 0.4 ? 'throw' : 'stand' });
    const tmpQ = new THREE.Quaternion();
    for (const d of this.dice) {
      if (d.settled) continue;
      const m = d.mesh;
      d.vel.y -= 9.8 * dt;
      m.position.addScaledVector(d.vel, dt);
      if (m.position.y < 0.035) {
        m.position.y = 0.035;
        d.vel.y = Math.abs(d.vel.y) * 0.4;
        d.vel.x *= 0.6;
        d.vel.z *= 0.6;
        d.spin.multiplyScalar(0.6);
        if (d.vel.y > 0.4 && this.bounced < 10) {
          this.bounced++;
          this.sfx('clank');
        }
      }
      // Tumble, then roll over onto the number it was always going to be.
      const k = Math.max(0, (this.t - (ROLL - 0.7)) / 0.7);
      tmpQ.setFromEuler(new THREE.Euler(d.spin.x * dt, d.spin.y * dt, d.spin.z * dt));
      m.quaternion.multiply(tmpQ);
      if (k > 0) m.quaternion.slerp(d.final, Math.min(1, k * k + 0.08));
      if (this.t >= ROLL) {
        m.quaternion.copy(d.final);
        m.position.y = 0.035;
        d.settled = true;
      }
    }
  }

  frame(camera: THREE.PerspectiveCamera): void {
    const p = this.player.position;
    // From the side, so you're both in the shot with the dice between you; closer once they land.
    const dx = this.spot.x - p.x;
    const dz = this.spot.z - p.z;
    const len = Math.hypot(dx, dz) || 1;
    const out = this.t > ROLL ? 1.3 : 2.4;
    camera.position.set(
      this.spot.x + (dz / len) * out - (dx / len) * 0.3,
      this.t > ROLL ? 0.95 : 1.7,
      this.spot.z - (dx / len) * out - (dz / len) * 0.3,
    );
    camera.lookAt(this.look.copy(this.spot).setY(this.t > ROLL ? 0.05 : 0.4));
  }

  hud(): StageHud {
    const a = this.mine[0] + this.mine[1];
    const b = this.theirs[0] + this.theirs[1];
    if (this.t < ROLL) return { score: ':dice: :dice:', status: `You vs. ${this.name}` };
    return {
      score: `${a} – ${b}`,
      status: `You (white) vs. ${this.name} (red)`,
      banner:
        a === b
          ? { text: 'PUSH', color: '#ffd400' }
          : a > b
            ? { text: 'WINNER!', color: '#9dff6a' }
            : { text: 'SNAKE BIT', color: '#ff8a8a' },
    };
  }

  end(): void {
    for (const d of this.dice) {
      d.mesh.removeFromParent();
      d.mesh.geometry.dispose();
    }
    this.player.scripted = false;
    this.player.syncTransform();
  }
}
